import { apiFetchWithRetry } from './apiFetch';

type SchoolRole = 'OWNER' | 'BILLING' | 'ADMIN' | 'COUNSELOR' | 'CLASSROOM_VIEWER';
type SchoolMembershipStatus = 'ACTIVE' | 'INACTIVE';

interface SchoolAdmin {
  id: string;
  name: string;
  email: string;
  title: string;
  status: string;
}

interface School {
  id: string;
  name: string;
  status: string;
  createdAt: string;
  /** Public URL of the school logo, if one has been set. */
  logoUrl?: string | null;
  schoolAdmin: SchoolAdmin;
  /** Email the latest SCHOOL_ADMIN invite was sent to; null if none sent yet. */
  invitedAdminEmail?: string | null;
  /** Id of the latest SCHOOL_ADMIN invite (for resend/cancel); null if none. */
  invitedAdminInviteId?: string | null;
  /** Status of the latest SCHOOL_ADMIN invite (PENDING/EXPIRED/CANCELLED/ACCEPTED); null if none. */
  invitedAdminInviteStatus?: string | null;
  studentCount?: number;
}

interface PaginationMeta {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

interface SchoolsResponse {
  data: School[];
  meta: PaginationMeta;
}

interface ExportSchoolsResponse {
  schools: School[];
  totalCount: number;
}

interface Invite {
  id: string;
  email: string;
  userRole: string;
  schoolRole?: SchoolRole | null;
  status: string;
  version: number;
  invitedBy: string;
  createdAt: string;
  updatedAt: string;
  expiresAt: string;
  schoolName: string;
  name: string;
  title: string;
}

type InvitesResponse = Invite[];

interface GetSchoolsParams {
  limit?: number;
  page?: number;
  search?: string;
  status?: string;
  /** true → schools with an accepted admin (Active tab);
   *  false → schools with no admin yet (Pending tab); omit for all. */
  hasAdmin?: boolean;
}

interface GetInvitesParams {
  userRole?: string;
  limit?: number;
  page?: number;
  search?: string;
  schoolId?: string;
  /** When true, server filters out invites with status=ACCEPTED. Useful
   *  when fetching the latest pending invite for a school to prefill the
   *  View/Edit admin modal. */
  excludeAccepted?: boolean;
}

interface GetOrganizationsParams {
  limit?: number;
  page?: number;
  search?: string;
}

interface Organization {
  name: string;
}

type OrganizationsResponse = Organization[];

interface InviteSchoolParams {
  email: string;
  schoolName: string;
  name: string;
  title: string;
  schoolRole?: Extract<SchoolRole, 'OWNER' | 'ADMIN'>;
}

interface InviteSchoolStaffParams {
  email: string;
  role: SchoolRole;
  name?: string;
  title?: string;
}

interface SchoolMembership {
  id: string;
  userId: string;
  schoolId: string;
  role: SchoolRole;
  status: SchoolMembershipStatus;
  invitedById: string | null;
  joinedAt: string | null;
  createdAt: string;
  updatedAt: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  title: string | null;
}

interface InviteSchoolResponse {
  success: boolean;
  message: string;
  data: {
    invites?: Invite[];
  } | null;
  error: null | {
    code?: number;
    detail: string;
    solution?: string;
  };
}

interface StudentExport {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  grade: string;
  dob: string | null;
  createdAt: string;
  status: string;
}

interface ExportStudentsResponse {
  students: StudentExport[];
  totalCount: number;
}

interface AdminDashboardStatsResponse {
  totalSchools: number;
  // Schools whose invited admin has accepted (schoolAdminId set on the API)
  // — matches the Active Schools tab count.
  activeSchools: number;
  // Schools added but with no accepted admin yet — matches the Pending
  // Schools tab count.
  pendingSchools: number;
  totalStudents: number;
  activeStudents: number;
  totalInvites: number;
}

// Lightweight directory entry surfaced in the Add School picker —
// schools students have already added via the freeform onboarding
// picker that no admin has formally onboarded yet. Picking one links
// the new School to the directory row via schoolDirectoryId so the
// two identities collapse.
export interface SchoolDirectoryLookup {
  id: string;
  name: string;
  city: string | null;
  region: string | null;
  country: string | null;
  website: string | null;
  studentCount: number;
}

export interface SchoolLookupResponse {
  operational: School[];
  directory: SchoolDirectoryLookup[];
}

// Result of scraping a school website for its logo — base64 bytes for an
// inline preview before the operator confirms saving it to the school.
export interface ExtractedLogo {
  base64Data: string;
  contentType: string;
  sourceUrl: string;
  // Best-effort school name parsed from the site (og:site_name / title).
  name?: string;
  // Unlinked directory entries whose website shares this URL's domain — used
  // to auto-link the correct same-named school instead of guessing by name.
  directoryMatches?: SchoolDirectoryLookup[];
}

export const schoolService = {
  // Step 1 of the 2-step add-school flow: create a real School row with
  // no admin yet. Backend route is POST /v1/schools/organizations and
  // takes a `names` array (it can batch-create); we send a single name.
  // When `schoolDirectoryId` is provided, the backend links the new
  // School row to that directory entry (no duplicates with the student-
  // seeded entry). Returns the created School so the caller can
  // immediately offer "invite admin" against its id.
  createSchool: async (name: string, schoolDirectoryId?: string): Promise<School> => {
    const response = await apiFetchWithRetry(`/v1/schools/organizations`, {
      method: 'POST',
      body: JSON.stringify({
        names: [name],
        ...(schoolDirectoryId ? { schoolDirectoryId } : {}),
      }),
    });
    const data = await response.json();

    if (!response.ok) {
      throw new Error(
        Array.isArray(data?.message) ? data.message.join(', ') : data?.message || 'Failed to create school',
      );
    }
    // Endpoint returns SchoolDto[]; we created exactly one.
    return Array.isArray(data) ? data[0] : data;
  },

  // Scrape a school website for its logo (preview only — nothing saved yet).
  // Returns base64 image bytes the modal renders as a preview; the operator
  // then confirms, and we persist via saveSchoolLogo after the school exists.
  extractLogoFromUrl: async (url: string): Promise<ExtractedLogo> => {
    const response = await apiFetchWithRetry(`/v1/schools/logo/extract`, {
      method: 'POST',
      body: JSON.stringify({ url }),
    });
    const data = await response.json();
    if (!response.ok) {
      throw new Error(
        Array.isArray(data?.message) ? data.message.join(', ') : data?.message || 'Failed to fetch logo from that URL',
      );
    }
    return data as ExtractedLogo;
  },

  // Persist a previewed/extracted logo onto a just-created school. Stores the
  // bytes server-side (R2) and sets logoUrl/logoKey on the school.
  saveSchoolLogo: async (schoolId: string, payload: { base64Data: string; contentType: string }): Promise<School> => {
    const response = await apiFetchWithRetry(`/v1/schools/${schoolId}/logo`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    const data = await response.json();
    if (!response.ok) {
      throw new Error(
        Array.isArray(data?.message) ? data.message.join(', ') : data?.message || 'Failed to save school logo',
      );
    }
    return data as School;
  },

  // Get schools (active and inactive)
  getSchools: async (params: GetSchoolsParams = {}): Promise<SchoolsResponse> => {
    const queryParams = new URLSearchParams();

    if (params.limit) queryParams.append('limit', params.limit.toString());
    if (params.page) queryParams.append('page', params.page.toString());
    if (params.search) queryParams.append('search', params.search);
    if (params.status && params.status !== 'all') queryParams.append('status', params.status);
    if (params.hasAdmin !== undefined) queryParams.append('hasAdmin', String(params.hasAdmin));

    const response = await apiFetchWithRetry(`/v1/schools?${queryParams.toString()}`);
    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Failed to fetch schools');
    }

    return data;
  },

  // Export schools
  // Backend returns SchoolDto[] directly; normalised to { schools, totalCount } for page's usage.
  exportSchools: async (params: GetSchoolsParams = {}): Promise<ExportSchoolsResponse> => {
    const queryParams = new URLSearchParams();

    // params sent but currently ignored by backend (exports all schools)
    if (params.search) queryParams.append('search', params.search);
    if (params.status && params.status !== 'all') queryParams.append('status', params.status);

    const response = await apiFetchWithRetry(`/v1/schools/export?${queryParams.toString()}`);
    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Failed to export schools');
    }

    const schools = Array.isArray(data) ? data : (data.schools || []);
    return {
      schools,
      totalCount: schools.length,
    };
  },

  // Export students by school ID
  // Backend returns StudentListItemDto[] directly; normalised to { students, totalCount } for page's usage.
  exportStudentsBySchool: async (schoolId: string): Promise<ExportStudentsResponse> => {
    const response = await apiFetchWithRetry(`/v1/students/export/${schoolId}`);
    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Failed to export students');
    }

    const students = Array.isArray(data) ? data : (data.students || []);
    return {
      students,
      totalCount: students.length,
    };
  },

  // Get invites (pending and expired)
  getInvites: async (params: GetInvitesParams = {}): Promise<InvitesResponse> => {
    const queryParams = new URLSearchParams();

    if (params.limit) queryParams.append('limit', params.limit.toString());
    if (params.page) queryParams.append('page', params.page.toString());
    if (params.search) queryParams.append('search', params.search);
    if (params.schoolId) queryParams.append('schoolId', params.schoolId);
    if (params.excludeAccepted) queryParams.append('excludeAccepted', 'true');
    if (params.userRole) queryParams.append('userRole', params.userRole);

    const response = await apiFetchWithRetry(`/v1/invites?${queryParams.toString()}`);
    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Failed to fetch invites');
    }

    return data;
  },

  // Add School picker dual-source lookup. Returns both already-onboarded
  // operational schools (existing behavior) AND SchoolDirectory entries
  // students have seeded via the freeform onboarding picker that no
  // admin has formally onboarded yet. The picker renders them as two
  // visually distinct sections; picking a directory row passes its id
  // back via createSchool's schoolDirectoryId arg so the new operational
  // School links to the existing directory entry instead of forming a
  // silent duplicate.
  lookupForCreate: async (params: { q: string; limit?: number }): Promise<SchoolLookupResponse> => {
    const queryParams = new URLSearchParams();
    queryParams.append('q', params.q);
    if (params.limit) queryParams.append('limit', String(params.limit));
    const response = await apiFetchWithRetry(`/v1/schools/lookup?${queryParams.toString()}`);
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data?.message || 'Failed to look up schools');
    }
    return data as SchoolLookupResponse;
  },

  // LLM web-search fallback for the Add School picker. Triggered by an
  // explicit operator click (NOT on-keystroke) because the underlying
  // OpenAI call is 2–5s. Returned entries are the same shape as the
  // directory half of lookupForCreate, so the picker treats them
  // identically — picking one links the new School to a freshly-
  // upserted SchoolDirectory row.
  webSearchForCreate: async (params: { q: string }): Promise<SchoolDirectoryLookup[]> => {
    const response = await apiFetchWithRetry(`/v1/schools/lookup/web-search`, {
      method: 'POST',
      body: JSON.stringify({ q: params.q }),
    });
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data?.message || 'Web search failed');
    }
    return data as SchoolDirectoryLookup[];
  },

  // Get organizations for school name dropdown
  getOrganizations: async (params: GetOrganizationsParams = {}): Promise<OrganizationsResponse> => {
    const queryParams = new URLSearchParams();

    if (params.limit) queryParams.append('limit', params.limit.toString());
    if (params.page) queryParams.append('page', params.page.toString());
    if (params.search) queryParams.append('search', params.search);

    const response = await apiFetchWithRetry(`/v1/schools/organizations?${queryParams.toString()}`);
    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Failed to fetch organizations');
    }

    return data;
  },

  // Invite school admin
  inviteSchool: async (params: InviteSchoolParams): Promise<InviteSchoolResponse> => {
    const response = await apiFetchWithRetry(`/v1/invites/schools`, {
      method: 'POST',
      body: JSON.stringify(params),
    });

    const data = await response.json();

    if (!response.ok) {
      return {
        success: false,
        message: data?.message || 'Failed to invite school admin',
        data: null,
        error: {
          code: data?.statusCode,
          detail: Array.isArray(data?.message) ? data.message.join(', ') : data?.message || 'Invite failed',
          solution: 'Please review the entered details and try again.',
        },
      };
    }

    return {
      success: true,
      message: 'Invite created successfully',
      data: {
        invites: data?.invites || [],
      },
      error: null,
    };
  },

  inviteSchoolStaff: async (schoolId: string, params: InviteSchoolStaffParams): Promise<InviteSchoolResponse> => {
    const response = await apiFetchWithRetry(`/v1/invites/schools/${schoolId}/staff`, {
      method: 'POST',
      body: JSON.stringify(params),
    });

    const data = await response.json();

    if (!response.ok) {
      return {
        success: false,
        message: data?.message || 'Failed to invite school staff',
        data: null,
        error: {
          code: data?.statusCode,
          detail: Array.isArray(data?.message) ? data.message.join(', ') : data?.message || 'Invite failed',
          solution: 'Please review the entered details and try again.',
        },
      };
    }

    return {
      success: true,
      message: 'Invite created successfully',
      data: {
        invites: data?.invites || [],
      },
      error: null,
    };
  },

  getSchoolMemberships: async (schoolId: string): Promise<SchoolMembership[]> => {
    const response = await apiFetchWithRetry(`/v1/schools/${schoolId}/memberships`);
    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Failed to fetch school memberships');
    }

    return data;
  },

  updateSchoolMembershipRole: async (schoolId: string, membershipId: string, role: SchoolRole): Promise<SchoolMembership> => {
    const response = await apiFetchWithRetry(`/v1/schools/${schoolId}/memberships/${membershipId}/role`, {
      method: 'PATCH',
      body: JSON.stringify({ role }),
    });
    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Failed to update membership role');
    }

    return data;
  },

  updateSchoolMembershipStatus: async (schoolId: string, membershipId: string, status: SchoolMembershipStatus): Promise<SchoolMembership> => {
    const response = await apiFetchWithRetry(`/v1/schools/${schoolId}/memberships/${membershipId}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    });
    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Failed to update membership status');
    }

    return data;
  },

  // Deactivate school
  deactivateSchool: async (schoolId: string): Promise<{ success: boolean; message: string; data: null }> => {
    const response = await apiFetchWithRetry(`/v1/schools/${schoolId}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status: 'INACTIVE' }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || data.error?.detail || 'Failed to deactivate school');
    }

    return data;
  },

  // Hard-delete a school. Server only permits this for schools with no
  // admin and no students (pending/junk rows); anything else 400s.
  deleteSchool: async (schoolId: string): Promise<{ id: string }> => {
    const response = await apiFetchWithRetry(`/v1/schools/${schoolId}`, {
      method: 'DELETE',
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || data.error?.detail || 'Failed to delete school');
    }

    return data;
  },

  // Resend invite
  resendInvite: async (inviteId: string): Promise<{ message: string; data: null; error: null }> => {
    const response = await apiFetchWithRetry(`/v1/invites/${inviteId}/resend`, {
      method: 'POST',
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || data.error?.detail || 'Failed to resend invite');
    }

    return data;
  },

  // Cancel a pending invite
  cancelInvite: async (inviteId: string): Promise<{ message?: string; data: null; error: null }> => {
    const response = await apiFetchWithRetry(`/v1/invites/${inviteId}/cancel`, {
      method: 'POST',
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || data.error?.detail || 'Failed to cancel invite');
    }

    return data;
  },

  // Admin dashboard stats
  getAdminDashboardStats: async (): Promise<AdminDashboardStatsResponse> => {
    const response = await apiFetchWithRetry(`/v1/dashboard/admins/stats`);
    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Failed to fetch dashboard stats');
    }

    return data;
  },
};

export type {
  School,
  Invite,
  SchoolRole,
  SchoolMembershipStatus,
  SchoolMembership,
  SchoolsResponse,
  ExportSchoolsResponse,
  ExportStudentsResponse,
  InvitesResponse,
  GetSchoolsParams,
  GetInvitesParams,
  GetOrganizationsParams,
  Organization,
  OrganizationsResponse,
  InviteSchoolParams,
  InviteSchoolStaffParams,
  InviteSchoolResponse,
  AdminDashboardStatsResponse,
};

